// AuthModal: modal con tabs login / signup.
// Patrón visual idéntico al ModalReserva (createPortal, modal-backdrop, modal-cerrar...).
//
// Tabs:
//   - login:  email + password
//   - signup: email + password + confirmar + nombre + (opcional) apellidos + (opcional) telefono
//
// Tras éxito → cierra. La rehidratación del context ya rellena <Nav /> con el avatar.

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { X, AlertCircle, Loader2 } from 'lucide-react';

import { useAuth } from '../../context/AuthContext';
import { ApiError } from '../../lib/api';

const RX_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function AuthModal({ initialTab = 'login', onClose }) {
  const { t, i18n } = useTranslation();
  const { login, signup } = useAuth();
  const navigate = useNavigate();

  const [tab, setTab] = useState(initialTab);
  const [enviando, setEnviando] = useState(false);
  const [errorGeneral, setErrorGeneral] = useState(null);
  const [errores, setErrores] = useState({});

  const [form, setForm] = useState({
    email: '',
    password: '',
    password2: '',
    nombre: '',
    apellidos: '',
    telefono: '',
  });

  // Bloqueo scroll
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  // Cerrar con Escape
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !enviando) onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, enviando]);

  const setCampo = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validar = () => {
    const errs = {};
    if (!RX_EMAIL.test(form.email)) errs.email = t('auth.errores.email_invalido');
    if (!form.password || form.password.length < 6) errs.password = t('auth.errores.password_corta');
    if (tab === 'signup') {
      if (form.password !== form.password2) errs.password2 = t('auth.errores.passwords_no_coinciden');
      if (!form.nombre.trim()) errs.nombre = t('auth.errores.nombre_requerido');
    }
    setErrores(errs);
    return Object.keys(errs).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    setErrorGeneral(null);
    if (!validar()) return;
    setEnviando(true);
    try {
      if (tab === 'login') {
        const data = await login(form.email.trim(), form.password);
        if (data.user?.rol === 'admin') navigate('/admin');
        else if (data.user?.rol === 'profesor') navigate('/profesor');
      } else {
        await signup({
          email: form.email.trim(),
          password: form.password,
          nombre: form.nombre.trim(),
          apellidos: form.apellidos.trim() || undefined,
          telefono: form.telefono.trim() || undefined,
          idioma_preferido: (i18n.resolvedLanguage || 'es').toLowerCase(),
        });
      }
      onClose();
    } catch (err) {
      if (err instanceof ApiError && err.code) {
        const traduccion = t('auth.errores.' + err.code, { defaultValue: '' });
        setErrorGeneral(traduccion || err.message || t('auth.errores.generico'));
      } else {
        setErrorGeneral(t('auth.errores.generico'));
      }
    } finally {
      setEnviando(false);
    }
  };

  const cambiarTab = (nuevoTab) => {
    if (enviando) return;
    setTab(nuevoTab);
    setErrores({});
    setErrorGeneral(null);
  };

  return createPortal(
    <div className="modal-backdrop" role="dialog" aria-modal="true" onClick={() => !enviando && onClose()}>
      <div className="modal modal-auth" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className="modal-cerrar"
          onClick={onClose}
          aria-label={t('comun.cerrar')}
          disabled={enviando}
        >
          <X size={20} strokeWidth={1.5} />
        </button>

        <div className="auth-tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'login'}
            className={'auth-tab' + (tab === 'login' ? ' active' : '')}
            onClick={() => cambiarTab('login')}
          >
            {t('auth.tab_login')}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'signup'}
            className={'auth-tab' + (tab === 'signup' ? ' active' : '')}
            onClick={() => cambiarTab('signup')}
          >
            {t('auth.tab_signup')}
          </button>
        </div>

        <form className="modal-form auth-form" onSubmit={submit} noValidate>
          <h2 className="modal-titulo">
            {tab === 'login' ? t('auth.login.titulo') : t('auth.signup.titulo')}
          </h2>
          <p className="auth-sub">
            {tab === 'login' ? t('auth.login.sub') : t('auth.signup.sub')}
          </p>

          <div className="modal-campo">
            <label htmlFor="a-email">{t('auth.email')}</label>
            <input
              id="a-email"
              type="email"
              autoComplete="email"
              value={form.email}
              onChange={setCampo('email')}
              placeholder={t('auth.email_placeholder')}
              aria-invalid={!!errores.email}
              disabled={enviando}
            />
            {errores.email && <div className="modal-error-campo">{errores.email}</div>}
          </div>

          <div className="modal-campo">
            <label htmlFor="a-pass">{t('auth.password')}</label>
            <input
              id="a-pass"
              type="password"
              autoComplete={tab === 'login' ? 'current-password' : 'new-password'}
              value={form.password}
              onChange={setCampo('password')}
              placeholder={t('auth.password_placeholder')}
              aria-invalid={!!errores.password}
              disabled={enviando}
            />
            {errores.password && <div className="modal-error-campo">{errores.password}</div>}
          </div>

          {tab === 'signup' && (
            <>
              <div className="modal-campo">
                <label htmlFor="a-pass2">{t('auth.password2')}</label>
                <input
                  id="a-pass2"
                  type="password"
                  autoComplete="new-password"
                  value={form.password2}
                  onChange={setCampo('password2')}
                  placeholder={t('auth.password2_placeholder')}
                  aria-invalid={!!errores.password2}
                  disabled={enviando}
                />
                {errores.password2 && <div className="modal-error-campo">{errores.password2}</div>}
              </div>

              <div className="modal-campo">
                <label htmlFor="a-nom">{t('auth.nombre')}</label>
                <input
                  id="a-nom"
                  type="text"
                  autoComplete="given-name"
                  value={form.nombre}
                  onChange={setCampo('nombre')}
                  placeholder={t('auth.nombre_placeholder')}
                  aria-invalid={!!errores.nombre}
                  disabled={enviando}
                />
                {errores.nombre && <div className="modal-error-campo">{errores.nombre}</div>}
              </div>

              <div className="auth-fila">
                <div className="modal-campo">
                  <label htmlFor="a-apel">{t('auth.apellidos')}</label>
                  <input
                    id="a-apel"
                    type="text"
                    autoComplete="family-name"
                    value={form.apellidos}
                    onChange={setCampo('apellidos')}
                    placeholder={t('auth.apellidos_placeholder')}
                    disabled={enviando}
                  />
                </div>
                <div className="modal-campo">
                  <label htmlFor="a-tel">{t('auth.telefono')}</label>
                  <input
                    id="a-tel"
                    type="tel"
                    autoComplete="tel"
                    value={form.telefono}
                    onChange={setCampo('telefono')}
                    placeholder={t('auth.telefono_placeholder')}
                    disabled={enviando}
                  />
                </div>
              </div>
            </>
          )}

          {errorGeneral && (
            <div className="modal-error-general">
              <AlertCircle size={16} strokeWidth={1.75} />
              <span>{errorGeneral}</span>
            </div>
          )}

          <div className="modal-acciones">
            <button type="submit" className="btn-primary modal-btn-principal" disabled={enviando}>
              {enviando ? (
                <><Loader2 size={16} className="spin" strokeWidth={1.5} /> {t('auth.enviando')}</>
              ) : (
                tab === 'login' ? t('auth.login.cta') : t('auth.signup.cta')
              )}
            </button>
          </div>

          <p className="auth-switch">
            {tab === 'login' ? (
              <>
                {t('auth.login.no_cuenta')}{' '}
                <button type="button" className="auth-link" onClick={() => cambiarTab('signup')} disabled={enviando}>
                  {t('auth.login.crea_cuenta')}
                </button>
              </>
            ) : (
              <>
                {t('auth.signup.tengo_cuenta')}{' '}
                <button type="button" className="auth-link" onClick={() => cambiarTab('login')} disabled={enviando}>
                  {t('auth.signup.entra')}
                </button>
              </>
            )}
          </p>
        </form>
      </div>
    </div>,
    document.body
  );
}
